// src/components/Profile/NotificationsPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCheck, RefreshCw } from 'lucide-react';
import { notificationsAPI } from '../../api/groupsApi';
import NotificationItem from './NotificationItem';
import './NotificationsPanel.css';

const NotificationsPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'invitations'

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'unread') params.unread = 'true';
      if (filter === 'invitations') params.type = 'group_invitation';
      const data = await notificationsAPI.getAll(params);
      setNotifications(Array.isArray(data) ? data : data.results ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationsAPI.getUnreadCount();
      setUnreadCount(data.unread_count ?? 0);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  // Poll unread count every 30 s
  useEffect(() => {
    const id = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(id);
  }, [fetchUnreadCount]);

  const handleMarkAllRead = async () => {
    await notificationsAPI.markAllRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleUpdate = (updated) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === updated.id ? updated : n))
    );
    fetchUnreadCount();
  };

  const handleDelete = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    fetchUnreadCount();
  };

  return (
    <div className="notifications-panel">
      {/* Header */}
      <div className="notifications-panel__header">
        <div className="notifications-panel__title">
          <Bell size={18} />
          <span>Notifications</span>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount}</span>
          )}
        </div>
        <div className="notifications-panel__header-actions">
          <button className="icon-btn" onClick={fetchNotifications} title="Refresh">
            <RefreshCw size={15} className={loading ? 'spinning' : ''} />
          </button>
          {unreadCount > 0 && (
            <button className="btn btn--sm btn--ghost" onClick={handleMarkAllRead}>
              <CheckCheck size={14} /> Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="notifications-panel__filters">
        {['all', 'unread', 'invitations'].map((f) => (
          <button
            key={f}
            className={`filter-tab ${filter === f ? 'filter-tab--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'unread' ? 'Unread' : 'Invitations'}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="notifications-panel__list">
        {loading && notifications.length === 0 && (
          <div className="notifications-panel__empty">
            <RefreshCw size={20} className="spinning" />
          </div>
        )}
        {!loading && notifications.length === 0 && (
          <div className="notifications-panel__empty">
            <Bell size={32} />
            <p>No notifications</p>
          </div>
        )}
        {notifications.map((n) => (
          <NotificationItem
            key={n.id}
            notification={n}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        ))}
      </div>
    </div>
  );
};

export default NotificationsPanel;
